import axios from "axios";
import { formatApiErrorBody } from "../utils/apiErrorMessage";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem("access") || localStorage.getItem("token");
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/** Nested user from GET /delivery/profile/ — `id` is the User pk for admin-ops staff routes */
export interface DeliveryProfileUser {
  id: number;
  phone?: string;
  full_name?: string;
  role?: string;
}

export interface DeliveryPartnerProfile {
  id: number;
  /** Present on GET /delivery/profile/ — true = checked in / on duty */
  is_available?: boolean;
  user?: DeliveryProfileUser;
  phone?: string;
  full_name?: string;
  role?: string;
  partner?: {
    id: number;
    vehicle_type: string;
    vehicle_number: string;
    store_id: number;
    store_name: string;
    is_available: boolean;
    total_deliveries: number;
    average_rating: string;
  };
}

/**
 * Get delivery partner profile
 * GET /delivery/profile/
 */
export const getDeliveryProfile = async (): Promise<DeliveryPartnerProfile> => {
  const response = await axios.get(`${BASE_URL}/delivery/profile/`, {
    headers: getAuthHeaders(),
  });

  // Handle nested response structure (response.data.data or response.data)
  const data = response.data?.data || response.data;
  return data;
};

/**
 * Update availability status (go on/off duty)
 * POST /api/v1/delivery/update-availability/
 * @param isAvailable - true to go on duty, false to go off duty
 */
export const updateAvailability = async (isAvailable: boolean): Promise<any> => {
  const response = await axios.post(
    `${BASE_URL}/delivery/update-availability/`,
    { is_available: isAvailable },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Update GPS location (call periodically during active delivery)
 * POST /api/v1/delivery/update-location/
 * @param latitude - Current latitude
 * @param longitude - Current longitude
 */
export const updateLocation = async (latitude: number, longitude: number): Promise<any> => {
  const response = await axios.post(
    `${BASE_URL}/delivery/update-location/`,
    { latitude, longitude },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Set password for the signed-in user (admin-ops staff set-password contract).
 * POST /api/v1/admin-ops/staff/<user_id>/set-password/
 * @param staffUserId User.id from delivery profile (`profile.user.id`), not DeliveryPartner.id
 */
export const setDeliveryPartnerPassword = async (
  staffUserId: number,
  password: string
): Promise<void> => {
  try {
    await axios.post(
      `${BASE_URL}/admin-ops/staff/${staffUserId}/set-password/`,
      { password },
      { headers: getAuthHeaders() }
    );
  } catch (err: unknown) {
    const ax = err as { response?: { data?: unknown } };
    const msg =
      formatApiErrorBody(ax?.response?.data) ||
      "Could not update password. Please try again.";
    throw new Error(msg);
  }
};
